import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {MenuSurfaceAnchor as RmwcMenuSurfaceAnchor} from '@rmwc/menu'
import {Typography as RmwcTypography} from '@rmwc/typography'
import {StyledRmwcBadge} from '../../badges/badge'
import {StyledRmwcBadgeAnchor} from '../../badges/badge-anchor'
import {StyledRmwcIcon} from '../../icons/icon'
import {StyledTextField} from '../text-field'
import {StyledCheckbox} from '../checkbox-field'
import {StyledRmwcMenu} from '../../menus/menu'
import {StyledRmwcMenuItem} from '../../menus/menu-item'

import '@rmwc/select/styles'

// eslint-disable-next-line no-unused-vars,react/prop-types
const FilteredTextField = ({items, ...otherProps}) => <StyledTextField {...otherProps} />
const StyledStyledTextField = styled(FilteredTextField)`
  &&& {
    cursor: pointer;
  }
  &&& .mdc-text-field__input {
    display: none;
  }
  &&& .mdc-text-field__icon--trailing {
    right: 12px;
    position: absolute;
  }

  ${(props) =>
    !!props.items &&
    props.items.length !== 0 &&
    `
    &&& .mdc-notched-outline__notch {
      padding-left: 0;
      padding-right: 0;
      border-top: none;
    }
    &&& .mdc-floating-label {
      transform: translateY(-26.75px) scale(0.75);
      font-size: 1rem;
    }
`}
`

const StyledStyledRmwcBadge = styled(StyledRmwcBadge)``

const StyledStyledRmwcBadgeAnchor = styled(StyledRmwcBadgeAnchor)`
  &&& {
    display: flex;
  }
`

const StyledStyledMenu = styled(StyledRmwcMenu)`
  &&& {
    width: 100%;
    max-width: initial;
  }
`

const StyledStyledOptionMenuItem = styled(StyledRmwcMenuItem)`
  &&& {
    padding: 0 16px 0 8px;
  }
`

const StyledStyledMenuItem = styled(StyledRmwcMenuItem)`
  &&& {
    height: initial;
    padding: 8px 16px;
    color: var(--mdc-theme-primary2);
  }
`

const StyledStyledCheckbox = styled(StyledCheckbox)`
  &&& > label {
    width: 100%;
  }
`

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
  padding-left: 16px;
  padding-right: 48px;
  overflow: hidden;
  height: 100%;
`

const StyledValueTypography = styled(RmwcTypography)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-family: 'Open Sans', sans-serif;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  letter-spacing: inherit;
  text-decoration: inherit;
  text-transform: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 19.1px;
`

const StyledClearAllTypography = styled(RmwcTypography)`
  font-family: 'Open Sans', sans-serif;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  letter-spacing: inherit;
  text-decoration: inherit;
  text-transform: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 19.1px;
`

const SelectMultiField = ({
  field: {name, value},
  form,
  meta,
  normalize = (value) => value,
  label,
  helperText,
  disabled = false,
  readOnly = false,
  isShowSelectAll = true,
  isShowClearAll = true,
  options,
  handleChange = () => {},
  outlined = true,
  ...otherProps
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [valueMap, setValueMap] = useState({})
  const [isAllChecked, setIsAllChecked] = useState(false)
  const [optionValueToOptionMap, setOptionValueToOptionMap] = useState({})

  useEffect(() => {
    if (value && value.length) {
      setValueMap(valueToValueMap(value))
    }
  }, [value])

  useEffect(() => {
    if (options && options.length) {
      setOptionValueToOptionMap(
        options.reduce((result, o) => {
          result[o.value] = o
          return result
        }, {})
      )
    }
  }, [options])

  const valueToValueMap = (_value) => {
    return (_value || []).reduce((result, key) => {
      result[key] = true
      return result
    }, {})
  }

  const valueMapToValue = (_valueMap) => {
    return Object.keys(_valueMap).reduce((result, key) => {
      if (_valueMap[key]) {
        result.push(key)
      }
      return result
    }, [])
  }

  function handleCheckAll(options) {
    const updatedValueMap = options.reduce((state, val) => {
      state[val.value] = !isAllChecked
      return state
    }, {})
    setValueMap({...updatedValueMap})
    setIsAllChecked(!isAllChecked)
    form.setFieldValue(name, valueMapToValue(updatedValueMap))
    handleChange(updatedValueMap)
  }

  function handleClearAll() {
    const updatedValueMap = {}
    setValueMap({...updatedValueMap})
    setIsAllChecked(false)
    form.setFieldValue(name, valueMapToValue(updatedValueMap))
    handleChange(updatedValueMap)
  }

  const error = form.errors[name] || meta?.error

  return (
    <div style={{width: '100%'}}>
      <StyledStyledTextField
        type="text"
        items={value}
        label={label}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        invalid={!!error}
        disabled={disabled}
        readOnly={readOnly}
        outlined={outlined}
        helpText={{
          persistent: true,
          ...(error && {validationMsg: true}),
          children: error ? error : helperText
        }}
        trailingIcon={{
          icon:
            Array.isArray(value) && value.length !== 0 ? (
              <StyledStyledRmwcBadgeAnchor>
                <StyledRmwcIcon icon="filter_list" style={{display: 'flex'}} />
                <StyledStyledRmwcBadge label={value.length} />
              </StyledStyledRmwcBadgeAnchor>
            ) : (
              <StyledRmwcIcon icon="filter_list" />
            ),
          onClick: () => setIsMenuOpen(!isMenuOpen)
        }}
        {...otherProps}>
        {value && (
          <StyledValueDiv>
            <StyledValueTypography tag="span">
              {Array.isArray(value) && value.length !== 0 && value.map((optionValue, index) => `${index !== 0 ? ', ' : ''}${optionValueToOptionMap[optionValue]?.label}`)}
            </StyledValueTypography>
          </StyledValueDiv>
        )}
      </StyledStyledTextField>
      <RmwcMenuSurfaceAnchor>
        <StyledStyledMenu focusOnOpen={false} open={isMenuOpen} onClose={() => setIsMenuOpen(false)} onOpen={() => setIsMenuOpen(true)}>
          {isShowSelectAll && (
            <StyledStyledOptionMenuItem key="isAllChecked" onClick={(e) => e.stopPropagation()}>
              <StyledStyledCheckbox checked={isAllChecked === true} onChange={() => handleCheckAll(options)} disabled={disabled} readOnly={readOnly}>
                All
              </StyledStyledCheckbox>
            </StyledStyledOptionMenuItem>
          )}
          {options &&
            options.map((o, index) => (
              <StyledStyledOptionMenuItem key={index} onClick={(e) => e.stopPropagation()}>
                <StyledStyledCheckbox
                  checked={valueMap[o.value] === true}
                  onChange={(e) => {
                    const normalizedValue = normalize(e.target.checked)
                    let state = {}
                    state[o.value] = normalizedValue
                    const updatedValueMap = {...valueMap, ...state}
                    setValueMap(updatedValueMap)
                    if (!normalizedValue) {
                      setIsAllChecked(false)
                    }
                    form.setFieldValue(name, valueMapToValue(updatedValueMap))
                    handleChange({...valueMap, ...state})
                  }}
                  disabled={disabled}
                  readOnly={readOnly}>
                  {o.label}
                  {o.description ? ` ${o.description}` : ''}
                </StyledStyledCheckbox>
              </StyledStyledOptionMenuItem>
            ))}
          {isShowClearAll && (
            <StyledStyledMenuItem
              key="clearAll"
              onClick={(e) => {
                e.stopPropagation()
                handleClearAll()
              }}>
              <StyledClearAllTypography>Clear all</StyledClearAllTypography>
            </StyledStyledMenuItem>
          )}
        </StyledStyledMenu>
      </RmwcMenuSurfaceAnchor>
    </div>
  )
}

SelectMultiField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.array
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  meta: PropTypes.object,
  label: PropTypes.string,
  helperText: PropTypes.string,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isShowSelectAll: PropTypes.bool,
  isShowClearAll: PropTypes.bool,
  handleChange: PropTypes.func,
  normalize: PropTypes.func,
  outlined: PropTypes.bool
}

export {SelectMultiField}
